import { debounce, isFunction } from '@agilepoint/utils-common';
import { PureComponent, createRef, Fragment } from 'react';
import styled from 'styled-components';
import { IListViewProps } from './listview-types';
import { ContentManager } from './content-manager';
import { FlexBox } from '../flex-box';
import { ensureSingleRequest, ListIterator, ResizeNotifierContext } from '../../utils';

const KEYS_TO_LISTEN: { [key: string]: string } = {
    enter: 'Enter',
    arrowUp: 'ArrowUp',
    arrowDown: 'ArrowDown'
};

const KEY_CODES = Object.values(KEYS_TO_LISTEN);

interface IListViewState {
    activeItemKey: React.Key;
    itemsPool: any[];
    scrollOffset: number;
    selectedKeys: React.Key[];
    containerDimensions: Pick<DOMRect, 'height' | 'width'>;
    isReady?: boolean;
}

class ListViewBase extends PureComponent<IListViewProps, IListViewState> {
    static contextType = ResizeNotifierContext;
    declare context: React.ContextType<typeof ResizeNotifierContext>;
    static defaultProps: Partial<IListViewProps> = {
        orientation: 'vertical',
        selectable: 'none'
    };

    private rootElementRef = createRef<HTMLDivElement>();
    private itemsIterator: ListIterator<any>;
    private canFetchMoreItems = true;

    private setScroll = (scrollPosition: number) => {
        const { current } = this.rootElementRef;
        if (current === null) {
            return;
        }
        if (this.isHorizontallyAligned()) {
            current.scrollLeft = scrollPosition;
            return;
        }
        current.scrollTo({
            top: scrollPosition,
            behavior: 'auto'
        });
    }

    static getDerivedStateFromProps(props: IListViewProps, state: IListViewState) {
        const { selectedKeys, items } = props;
        const newState: Partial<IListViewState> = {};
        if (selectedKeys !== undefined) {
            newState.selectedKeys = selectedKeys;
        }
        if (items !== undefined && items !== state.itemsPool) {
            newState.itemsPool = items;
        }
        return Object.keys(newState).length ? newState : null;
    }

    constructor(props: IListViewProps) {
        super(props);
        if (props.elementRef) {
            this.rootElementRef = props.elementRef;
        }
        const { items = [], defaultSelectedKeys = [], selectedKeys, initialItems = [], itemsIterator } = this.props;
        this.itemsIterator = (itemsIterator ?? new ListIterator(items, { canSelectItem: this.canSelectItem })) as ListIterator<any>;
        this.state = {
            activeItemKey: '',
            itemsPool: props.items === undefined ? initialItems : items,
            scrollOffset: 0,
            selectedKeys: selectedKeys === undefined ? defaultSelectedKeys : selectedKeys,
            containerDimensions: this.getContainerRect()
        };
    }

    render() {
        const {
            className, buffer = 10, itemHeight = 0, itemWidth = 0, keyProp, onRenderItem,
            onRenderItemContent, selectable, hidden, hideLoader, autoResizeThreshold, getItemSize
        } = this.props;
        const { activeItemKey, itemsPool, scrollOffset, selectedKeys, containerDimensions, isReady } = this.state;
        const isHorizontallyAligned = this.isHorizontallyAligned();
        return hidden ? null : (
            <Fragment>
                <FlexBox
                    inline
                    flexDirection={isHorizontallyAligned ? 'column' : 'row'}
                    ref={this.rootElementRef}
                    tabIndex={this.isSelectable() ? 0 : -1}
                    onKeyDown={this.handleKeyboardNavigation}
                    onBlur={this.onBlur}
                    className={className}
                    onScroll={this.onScroll}>
                    {isReady
                        ? <ContentManager activeItemKey={activeItemKey} buffer={buffer}
                            containerDimensions={containerDimensions} setScroll={this.setScroll}
                            getKey={this.getKey} itemHeight={itemHeight} itemWidth={itemWidth} keyProp={keyProp}
                            selectable={selectable!} selectedKeys={selectedKeys} isVirtualized={this.isVirtualized()}
                            items={itemsPool} isHorizontallyAligned={isHorizontallyAligned}
                            onItemSelect={this.onItemSelect} onRenderItem={onRenderItem}
                            autoResizeThreshold={autoResizeThreshold} getItemSize={getItemSize}
                            onRenderItemContent={onRenderItemContent} hideLoader={hideLoader}
                            scrollOffset={scrollOffset} canSelectItem={this.canSelectItem} />
                        : null}
                </FlexBox>
                {this.renderFetchMore()}
            </Fragment>
        );
    }

    componentDidMount() {
        this.itemsIterator.subscribeToOnChange(this.setActiveItem);
        const { current } = this.rootElementRef;
        if (!current) {
            return;
        }
        this.updateContainerDimensions(this.getContainerRect());
        this.context.subscribe(current, this.updateContainerDimensions);
    }

    componentDidUpdate(prevProps: IListViewProps) {
        const { items } = this.props;
        if (items && items !== prevProps.items) {
            this.itemsIterator.updateList(items);
        }
    }

    componentWillUnmount() {
        this.itemsIterator.unsubscribeToOnChange(this.setActiveItem);
        const { current } = this.rootElementRef;
        if (!current) {
            return;
        }
        this.context.unsubscribe(current, this.updateContainerDimensions);
    }

    private renderFetchMore() {
        const { onRenderFetchMore } = this.props;
        const { itemsPool } = this.state;
        return onRenderFetchMore
            ? onRenderFetchMore({
                fetchMore: this.fetchMore,
                loadedItemsLength: itemsPool.length
            }) : null;
    }

    private isVirtualized = () => {
        const { isVirtualized } = this.props;
        const { itemsPool } = this.state;
        if (isVirtualized === undefined) {
            return itemsPool.length > 50;
        }
        return isVirtualized;
    }

    private isHorizontallyAligned = () => {
        const { orientation } = this.props;
        return orientation === 'horizontal';
    }

    private onItemSelect = (item: any) => {
        const { selectedKeys } = this.state;
        let newSelectedKeys = this.isMultiSelectEnabled() ? selectedKeys.slice() : [];
        const selectedKey = this.getKey(item);
        if (selectedKeys.includes(selectedKey)) {
            newSelectedKeys = selectedKeys.filter(key => selectedKey !== key);
        } else {
            newSelectedKeys.push(selectedKey);
        }
        this.onChange(newSelectedKeys);
        this.setState({ activeItemKey: selectedKey });
        this.itemsIterator.find(i => this.getKey(i) === selectedKey);
    }

    private isSelectable() {
        const { selectable } = this.props;
        return (selectable !== undefined && selectable !== 'none');
    }

    private isMultiSelectEnabled() {
        const { selectable } = this.props;
        return selectable === 'multiple';
    }

    private onChange = (selectedKeys: React.Key[]) => {
        const { onChange, selectedKeys: propsHasSelectedKeys } = this.props;
        if (!propsHasSelectedKeys) {
            this.setState({
                selectedKeys
            });
        }
        if (onChange) {
            onChange(selectedKeys);
        }
    }

    private handleKeyboardNavigation = (ev: React.KeyboardEvent) => {
        const { allowKeyboardNavigation } = this.props;
        ev.stopPropagation();
        if (!allowKeyboardNavigation || !this.isSelectable() || KEY_CODES.indexOf(ev.key) === -1) {
            return;
        }
        ev.preventDefault();
        switch (ev.key) {
            case KEYS_TO_LISTEN['arrowUp']:
                this.itemsIterator.prev();
                break;
            case KEYS_TO_LISTEN['arrowDown']:
                this.itemsIterator.next();
                break;
            case KEYS_TO_LISTEN['enter']:
                this.onItemSelect(this.itemsIterator.getValue());
                break;
            default: break;
        }
    }

    private getContainerRect = (): Pick<DOMRect, 'height' | 'width'> => {
        const { current } = this.rootElementRef;
        return current?.getBoundingClientRect() ?? { height: 0, width: 0 };
    }

    private updateContainerDimensions = debounce(({ height, width }: Pick<DOMRect, 'height' | 'width'>) => {
        this.setState({ containerDimensions: { height, width }, isReady: true });
    }, 50);

    private getScrollSize() {
        const { current } = this.rootElementRef;
        if (current === null) {
            return 0;
        }
        return this.isHorizontallyAligned() ? current.scrollWidth : current.scrollHeight;
    }

    private setActiveItem = (item: any) => {
        const activeItemKey = item === undefined ? '' : this.getKey(item);
        this.setState({
            activeItemKey
        });
    }

    private onBlur = () => {
        this.setState({
            activeItemKey: ''
        });
        this.itemsIterator.reset();
    }

    private onScroll = async (ev: React.UIEvent<HTMLDivElement>) => {
        const { loadOnDemand, onRenderFetchMore, onScroll } = this.props;
        const isVerticallyAligned = !this.isHorizontallyAligned();
        const { currentTarget } = ev;
        const scrollOffset = isVerticallyAligned ? currentTarget.scrollTop : currentTarget.scrollLeft;
        const totalSize = this.getScrollSize();
        const { height, width } = this.getContainerRect();
        const scrollPosition = scrollOffset + (isVerticallyAligned ? height : width);

        if (onScroll) {
            onScroll(ev);
        }

        if (!onRenderFetchMore && loadOnDemand === true && (scrollPosition / totalSize) > 0.9) {
            await this.fetchMore();
        }

        if (this.isVirtualized()) {
            this.updateScroll(currentTarget);
        }
    }

    private updateScroll(target: HTMLDivElement) {
        const isHorizontallyAligned = this.isHorizontallyAligned();
        const newScrollOffset = isHorizontallyAligned ? target.scrollLeft : target.scrollTop;
        this.setState({
            scrollOffset: newScrollOffset
        });
    }

    private fetchMore = ensureSingleRequest(async () => {
        const { onFetchMoreItems, loadOnDemand } = this.props;

        if (!loadOnDemand || onFetchMoreItems === undefined || this.canFetchMoreItems === false) {
            return;
        }

        const newItems = await onFetchMoreItems();

        if (newItems === null) {
            this.canFetchMoreItems = false;
            return;
        }

        if (newItems.length === 0) {
            return;
        }

        const { itemsPool } = this.state;
        const itemsToDisplay = itemsPool.concat(newItems);
        this.setState({
            itemsPool: itemsToDisplay
        });
        this.itemsIterator.updateList(itemsToDisplay);
        this.canFetchMoreItems = true;
    });

    private canSelectItem = (item: any) => {
        const { canSelectItem } = this.props;
        return canSelectItem === undefined ? true : canSelectItem(item);
    }

    private getKey = (item: any): React.Key => {
        const { getKey, keyProp } = this.props;
        if (isFunction(getKey)) {
            return getKey(item);
        }
        if (keyProp !== undefined) {
            return (item)[keyProp];
        }
        return item;
    }
}

export const ListView = styled(ListViewBase).attrs(() => ({
    className: String('ap-listView-wrapper')
}))`
    position: relative;
    overflow: auto;
    height: 100%;
    width: 100%;
    border-style: solid;
    box-sizing: border-box;
    border-color: ${({ theme: { listView: { colors } } }) => colors.borderColor};
    border-width: ${({ theme: { listView: { composites: { root } } } }) => root.borderWidth};
    border-radius: ${({ theme: { listView: { composites: { root } } } }) => root.borderRadius};
    box-shadow: ${({ theme: { listView: { composites: { root } } } }) => root.boxShadow};
`;
